'use client';

import { useOptimizely } from '@/main/lib/hooks/useOptimizely';
import { useEffect } from 'react';
import { trackOptimizelyEvent } from '@/app_lib/optimizely/utils';
import { usePaywall } from '@/app_lib/providers/PaywallProvider';

const TRINITY_ID = 2900017491; // not secret
const TRINITY_READ_ID = 2900023830; // not secret

function getPlayerType(paywallCheck: string) {
  return paywallCheck === 'no-paywall' ? '' : 'subscription-locked';
}

function buildScriptSrc(url: string, playerType: string, ownAudio?: string, trinityId: number = TRINITY_ID, subscriptionLockedUrl?: string) {
  let src = `https://trinitymedia.ai/player/trinity/${trinityId}/?pageURL=${encodeURIComponent(
    url,
  )}&FAB=fabAfterClick&playerType=${playerType}`;
  if (ownAudio) {
    src += `&cf=${encodeURIComponent(ownAudio)}`;
  }
  if (subscriptionLockedUrl) {
    src += `&subscriptionLockedURL=${encodeURIComponent(subscriptionLockedUrl)}`;
  }

  return src;
}

export interface TrinityPlayerProps {
  url: string;
  ownAudio?: string;
  noTrinityAds?: boolean;
  subscriptionLockedUrl?: string;
}

// NOTE: ownAudio comes from earnings reports

export const TrinityPlayerScript = ({ url, ownAudio, noTrinityAds, subscriptionLockedUrl }: TrinityPlayerProps) => {
  const {
    paywallState: { paywallCheck },
  } = usePaywall();

  const { userFlags: userVariants, optimizely } = useOptimizely();

  const trinityReadAlongFeature =
    (
      userVariants?.trinity_read_along_feature as {
        variant: string;
      }
    )?.variant === 'on';

  useEffect(() => {
    if (window.__TRINITY_SCRIPT_LOADED__) {
      return;
    }

    const trinityRef = document.querySelector('div.impression-element[data-content-placement="trinity_audio_player"]');
    const shouldInitPlayer = paywallCheck !== 'pending' && trinityRef;

    let script: HTMLScriptElement | null = null;

    if (shouldInitPlayer) {
      const playerType = getPlayerType(paywallCheck);
      script = document.createElement('script');
      const trinityId = trinityReadAlongFeature ? TRINITY_READ_ID : TRINITY_ID;
      script.src = buildScriptSrc(url, playerType, ownAudio, trinityId, subscriptionLockedUrl);
      script.async = true;
      script.type = 'text/javascript';

      if (trinityRef) {
        trinityRef.appendChild(script);
        window.__TRINITY_SCRIPT_LOADED__ = true;
      }
    }
  }, [url, paywallCheck, ownAudio]);

  useEffect(() => {
    const updateTrinityAdStatus = (event: MessageEvent) => {
      if (
        event.origin !== 'https://trinitymedia.ai' ||
        event.data?.type !== 'TRINITY_TTS' ||
        typeof window === 'undefined' ||
        !window.TRINITY_PLAYER ||
        !window.TRINITY_PLAYER.message ||
        !window.TRINITY_PLAYER.api
      ) {
        return;
      }
      if (event.data.value && event.data.value.action === window.TRINITY_PLAYER.message.playerReady && noTrinityAds) {
        window.TRINITY_PLAYER.api.setAdFreeSubscriber();
      }
    };
    window.addEventListener('message', updateTrinityAdStatus);
    return () => {
      window.removeEventListener('message', updateTrinityAdStatus);
    };
  }, [noTrinityAds]);

  // Trinity player click tracking
  useEffect(() => {
    const handleTrinityClick = (event: Event) => {
      const target = event.target as HTMLElement;

      // Check if click is within Trinity player elements
      const isTrinityClick =
        target.closest('.trinity-tts-pb') ||
        target.closest('.trinity-player-iframe-wrapper') ||
        target.closest('.trinity-iframe') ||
        target.closest('[data-content-placement="trinity_audio_player"]');

      if (isTrinityClick) {
        trackOptimizelyEvent(optimizely, 'trinity_player_click', {
          elementClicked: target.className || target.tagName,
          pageUrl: url,
          playerType: trinityReadAlongFeature ? 'read-along' : 'default',
          trinityId: trinityReadAlongFeature ? TRINITY_READ_ID : TRINITY_ID,
          value: 1,
        });
      }
    };

    // Use event delegation to catch clicks on dynamically created Trinity elements
    document.addEventListener('click', handleTrinityClick, true);

    return () => {
      document.removeEventListener('click', handleTrinityClick, true);
    };
  }, [optimizely, trinityReadAlongFeature, url]);

  // Scroll depth tracking
  useEffect(() => {
    const thresholds = [25, 50, 75, 90];
    const tracked = new Set<number>();

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) {
        return;
      }
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);
      for (const threshold of thresholds) {
        if (scrollPercent >= threshold && !tracked.has(threshold)) {
          tracked.add(threshold);
          trackOptimizelyEvent(optimizely, 'scroll_depth', {
            pageUrl: url,
            playerType: trinityReadAlongFeature ? 'read-along' : 'default',
            scrollDepthPercent: threshold,
            trinityId: trinityReadAlongFeature ? TRINITY_READ_ID : TRINITY_ID,
            value: threshold,
          });
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [optimizely, trinityReadAlongFeature, url]);

  return (
    <div
      className='trinity-tts-pb'
      dir='ltr'
    />
  );
};
